import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type { ESigCertificate } from "@/services/(public)/signLinkApi";

// HR-side signature lookup. Lets the employment-documents listing surface
// signed sigs and pull the matching certificate without a sign-link session.
// The cert endpoint itself is public (DocumentPreviewController on the BE) so
// no extra auth wiring is needed.

export interface DocumentSignatureRow {
  id: number;
  documentId: number;
  status: "unsigned" | "signed" | "rejected";
  signedAt: string | null;
  signerId: number | null;
  signerCandidateId: number | null;
}

export const documentSignaturesApi = createApi({
  reducerPath: "documentSignaturesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["DocumentSignatures", "Certificate"],
  endpoints: (builder) => ({
    listByDocument: builder.query<
      IApiResponse<DocumentSignatureRow[]>,
      { documentId: number }
    >({
      query: ({ documentId }) => ({
        url: `/documents-signature/${documentId}/signatures`,
        method: "GET",
      }),
      providesTags: (_r, _e, { documentId }) => [
        { type: "DocumentSignatures", id: documentId },
      ],
    }),

    getCertificate: builder.query<
      IApiResponse<ESigCertificate>,
      { signatureId: number }
    >({
      query: ({ signatureId }) => ({
        url: `/documents-signature/${signatureId}/certificate`,
        method: "GET",
      }),
      providesTags: (_r, _e, { signatureId }) => [
        { type: "Certificate", id: signatureId },
      ],
    }),

    /**
     * Combined cert across multiple signatures (e.g. every doc signed in a
     * single batch). Cache-keyed by sorted-id list so duplicate orderings
     * share the response.
     */
    getCombinedCertificate: builder.query<
      IApiResponse<ESigCertificate>,
      { signatureIds: number[] }
    >({
      query: ({ signatureIds }) => ({
        url: `/documents-signature/certificate/combined`,
        method: "GET",
        params: { ids: [...signatureIds].sort((a, b) => a - b).join(",") },
      }),
      providesTags: (_r, _e, { signatureIds }) =>
        signatureIds.map((id) => ({ type: "Certificate" as const, id })),
    }),

    /**
     * Resolve the stored (immutable) signed PDF URL captured at sign time.
     * If the BE has no stored URL it returns a 400 — there's no live-render
     * fallback; legacy sigs must be regenerated and re-signed.
     */
    getSignedPdfUrl: builder.query<
      IApiResponse<{
        url: string;
        hash: string | null;
        signedAt: string | null;
      }>,
      { signatureId: number }
    >({
      query: ({ signatureId }) => ({
        url: `/company/document-templates/signed/${signatureId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useListByDocumentQuery,
  useLazyListByDocumentQuery,
  useGetCertificateQuery,
  useLazyGetCertificateQuery,
  useGetCombinedCertificateQuery,
  useLazyGetCombinedCertificateQuery,
  useLazyGetSignedPdfUrlQuery,
} = documentSignaturesApi;
